import { DEFAULT_PAGE_SIZE } from "@/core/constants/pagination";
import type { HttpQueryParam, HttpRequestConfig, IHttpClient } from "@/core/interfaces";
import {
  extractItems,
  extractTotal,
  isRecord,
  readFirstText,
  toPaginatedQuery,
} from "@/core/utils";
import {
  appendExportColumns,
  appendReportQuery,
  parseContentDispositionFileName,
} from "@/modules/export";
import type { ExportedFile, ExportFormat, XlsxLayout } from "@/modules/export";
import { Registry } from "@/shared/infra/dependency-injection/registry";
import { EMBARCACOES_EXPORT_COLUMN_KEYS } from "./embarcacoes.constants";
import { DEFAULT_EMBARCACOES_SORT, EMBARCACAO_FILTER_COLUMNS } from "./embarcacoes.types";
import type {
  DownloadEmbarcacaoImagemParams,
  Embarcacao,
  EmbarcacaoAcomodacoes,
  EmbarcacaoAcomodacaoTipo,
  EmbarcacaoBaseTecnica,
  EmbarcacaoClasse,
  EmbarcacaoComercial,
  EmbarcacaoContatos,
  EmbarcacaoDimensoes,
  EmbarcacaoConsumoOperacao,
  EmbarcacaoFilterColumn,
  EmbarcacaoFilterOption,
  EmbarcacaoGuinchoGuindaste,
  EmbarcacaoImagemArquivo,
  EmbarcacaoImagemTipo,
  EmbarcacaoImagens,
  EmbarcacaoMotor,
  EmbarcacaoPropulsao,
  EmbarcacaoEquipamentos,
  EmbarcacaoFicha,
  EmbarcacaoSistemasDp,
  EmbarcacaoTanques,
  EmbarcacaoThruster,
  EmbarcacaoVazaoBombas,
  EmbarcacaoVelocidadeConsumo,
  EmbarcacoesPage,
  ExportEmbarcacoesParams,
  GetEmbarcacaoFichaParams,
  ListEmbarcacaoFilterOptionsParams,
  ListEmbarcacoesParams,
} from "./embarcacoes.types";

/** Recursos do contrato da HU 812, expostos pelo hub-bff (tarefa 1802). */
export const EMBARCACOES_PATHS = {
  LIST: "/api/embarcacoes",
  EXPORT: "/api/embarcacoes/exportacao",
} as const;

/** Opções de um filtro de coluna: um serviço por coluna filtrável. */
export function buildEmbarcacaoFilterOptionsPath(column: EmbarcacaoFilterColumn): string {
  return `${EMBARCACOES_PATHS.LIST}/filtros/${column}`;
}

/** Exportação do detalhamento completo da embarcação (HU 2388); apenas GET. */
export function buildEmbarcacaoExportacaoPath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/exportacao`;
}

/** Ficha completa do detalhamento: as 17 seções numa única consulta; apenas GET. */
export function buildEmbarcacaoFichaPath(id: string): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/all`;
}

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

const COLLECTION_KEYS = ["embarcacoes", "opcoes", "filtros"];

function toEmbarcacao(raw: unknown, index: number): Embarcacao | null {
  if (!isRecord(raw)) return null;

  const acronimo = readFirstText(raw, "acronimo", "acronym", "sigla").trim();
  const nome = readFirstText(raw, "nome", "embarcacao", "name").trim();
  const tipo = readFirstText(raw, "tipo", "type").trim();
  const situacao = readFirstText(raw, "situacao", "situation", "status").trim();
  if (!acronimo && !nome && !tipo) return null;

  return {
    // O índice mantém a linha endereçável (chave da tabela) quando não vem id.
    id: readFirstText(raw, "id", "embarcacaoId").trim() || `${acronimo || nome}-${index}`,
    acronimo,
    nome,
    tipo,
    situacao,
  };
}

/** Descarta valores vazios: um filtro sem valor não restringe a consulta. */
function cleanValues(values: string[] | undefined): string[] {
  return (values ?? []).map((value) => value.trim()).filter((value) => value !== "");
}

/** Busca e filtros de coluna, comuns à listagem e à exportação da visualização. */
function buildCriteriaParams(
  busca: string | undefined,
  filters: ListEmbarcacoesParams["filters"],
): Record<string, HttpQueryParam> {
  const params: Record<string, HttpQueryParam> = {};

  const term = busca?.trim();
  if (term) params.busca = term;

  for (const column of EMBARCACAO_FILTER_COLUMNS) {
    const values = cleanValues(filters?.[column]);
    if (values.length > 0) params[column] = values;
  }

  return params;
}

/**
 * Consulta somente leitura da frota (RN001/RN003).
 *
 * Contrato da HU 812: `busca` em texto livre, os filtros de coluna como listas
 * (`acronimo[]`, `embarcacao[]`, `tipo[]`, `situacao[]`), paginação 1-based e ordenação no contrato paginado
 * (`page`, `pageSize`, `sort_by`, `sort_direction`). Os critérios são combinados
 * em conjunto (RN004) e o recorte por privilégio é aplicado pelo próprio serviço.
 */
export async function listEmbarcacoes(
  request: ListEmbarcacoesParams = {},
): Promise<EmbarcacoesPage> {
  const {
    busca,
    filters,
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    sort = DEFAULT_EMBARCACOES_SORT,
    signal,
  } = request;

  const params = buildCriteriaParams(busca, filters);

  Object.assign(params, toPaginatedQuery({ page, pageSize, sort }));

  const config: HttpRequestConfig = { signal, params };

  const response = await getHttpClient().get<unknown>(EMBARCACOES_PATHS.LIST, config);

  const embarcacoes = extractItems(response.data, COLLECTION_KEYS)
    .map(toEmbarcacao)
    .filter((embarcacao): embarcacao is Embarcacao => embarcacao !== null);

  return { embarcacoes, total: extractTotal(response.data) ?? embarcacoes.length };
}

/**
 * Exportar Visualização da listagem (HU de exportação — privilégio
 * `hub.embarcacao:exportar`, verificado pelo serviço).
 *
 * Mesmos critérios da consulta da HU 812 — `busca`, filtros de coluna e ordenação —,
 * sem paginação: o arquivo traz o conjunto completo do resultado (RN003). O
 * formato segue a estrutura de exportação da US 1442, no parâmetro `formato`.
 */
export async function exportEmbarcacoes({
  busca,
  filters,
  filtros,
  colunas,
  sort = DEFAULT_EMBARCACOES_SORT,
  format,
  signal,
}: ExportEmbarcacoesParams): Promise<ExportedFile> {
  const params = buildCriteriaParams(busca, filters);

  Object.assign(params, toPaginatedQuery({ sort }));
  params.formato = format;
  appendExportColumns(params, colunas, EMBARCACOES_EXPORT_COLUMN_KEYS, "Embarcações");
  appendReportQuery(params, filtros);

  const response = await getHttpClient().get<ArrayBuffer>(EMBARCACOES_PATHS.EXPORT, {
    signal,
    params,
    responseType: "arraybuffer",
  });

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

/**
 * Exportar o detalhamento de uma embarcação (HU 2388 — privilégio
 * `hub.embarcacao:exportar`, verificado pelo serviço, que responde 403 sem ele).
 *
 * O arquivo é gerado no servidor com todas as seções do detalhamento, no
 * formato pedido em `formato`; o nome vem do `Content-Disposition`.
 */
export async function exportEmbarcacaoDetalhe(
  id: string,
  format: ExportFormat,
  options?: { signal?: AbortSignal; xlsxLayout?: XlsxLayout },
): Promise<ExportedFile> {
  const response = await getHttpClient().get<ArrayBuffer>(buildEmbarcacaoExportacaoPath(id), {
    signal: options?.signal,
    params: {
      formato: format,
      ...(format === "xlsx" && options?.xlsxLayout ? { layout: options.xlsxLayout } : {}),
    },
    responseType: "arraybuffer",
  });

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? null,
  };
}

/** Comparação de texto no idioma da tela: sem distinguir acento nem caixa. */
const COLLATOR = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true });

/** `OpcaoFiltroDTO` do contrato: `valor` e `rotulo`. O rótulo cai no valor quando ausente. */
function toFilterOption(raw: unknown): EmbarcacaoFilterOption | null {
  if (!isRecord(raw)) {
    const value = typeof raw === "string" ? raw.trim() : "";
    return value ? { value, label: value } : null;
  }

  const value = readFirstText(raw, "valor", "value", "id", "codigo").trim();
  if (!value) return null;

  return {
    value,
    label: readFirstText(raw, "rotulo", "label", "descricao", "nome").trim() || value,
  };
}

/**
 * Opções de uma coluna filtrável, já ordenadas pelo rótulo e sem repetição — o
 * serviço é a única origem das opções, que não dependem da página em tela.
 */
export async function listEmbarcacaoFilterOptions(
  request: ListEmbarcacaoFilterOptionsParams,
): Promise<EmbarcacaoFilterOption[]> {
  const { column, signal } = request;

  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(
    buildEmbarcacaoFilterOptionsPath(column),
    config,
  );

  const seen = new Set<string>();

  return extractItems(response.data, COLLECTION_KEYS)
    .map(toFilterOption)
    .filter((option): option is EmbarcacaoFilterOption => {
      if (option === null || seen.has(option.value)) return false;

      seen.add(option.value);
      return true;
    })
    .sort((first, second) => COLLATOR.compare(first.label, second.label));
}

/**
 * Campo de uma seção do detalhamento: `null` quando a fonte não tem o dado.
 *
 * Um número do contrato (ano, idade, POB) vira o seu próprio texto — nenhum
 * cálculo ou derivação é aplicado (RN005) —, e texto em branco equivale a
 * campo sem preenchimento.
 */
function readValue(raw: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = raw[key];

    if (typeof value === "string" && value.trim() !== "") return value.trim();
    if (typeof value === "number" && Number.isFinite(value)) return String(value);
  }

  return null;
}

function toEmbarcacaoBaseTecnica(raw: unknown): EmbarcacaoBaseTecnica | null {
  if (!isRecord(raw)) return null;

  return {
    nome: readValue(raw, "nome", "embarcacao"),
    nomeAntigo: readValue(raw, "nomeAntigo"),
    nomeAbreviado: readValue(raw, "nomeAbreviado"),
    acronimo: readValue(raw, "acronimo"),
    tipo: readValue(raw, "tipo"),
    empresaOrigem: readValue(raw, "empresaOrigem"),
    mmsi: readValue(raw, "mmsi"),
    vesselSignalLetters: readValue(raw, "vesselSignalLetters"),
    imo: readValue(raw, "imo"),
    grupo: readValue(raw, "grupo"),
    cnpjFaturamento: readValue(raw, "cnpjFaturamento"),
    // O protótipo chama os dois campos de "Nome do Armador" e "CNPJ do Armador".
    armador: readValue(raw, "armador", "nomeArmador"),
    cnpj: readValue(raw, "cnpj", "cnpjArmador"),
    centroCusto: readValue(raw, "centroCusto"),
    sisterVessels: readValue(raw, "sisterVessels"),
    possuiReb: readValue(raw, "possuiReb"),
    bandeiraSuspensa: readValue(raw, "bandeiraSuspensa"),
    bandeira: readValue(raw, "bandeira"),
    paisConstrucao: readValue(raw, "paisConstrucao"),
    estaleiroConstrucao: readValue(raw, "estaleiroConstrucao"),
    anoBatimentoQuilha: readValue(raw, "anoBatimentoQuilha"),
    anoFinalConstrucao: readValue(raw, "anoFinalConstrucao"),
    idade: readValue(raw, "idade"),
    anoModernizacao: readValue(raw, "anoModernizacao"),
    sistemaGerencial: readValue(raw, "sistemaGerencial"),
    totalPessoas: readValue(raw, "totalPessoas"),
    situacaoEmbarcacao: readValue(raw, "situacaoEmbarcacao", "situacao"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

/**
 * Campo dimensional: o número como armazenado na fonte, sem arredondar nem
 * converter unidade (RN005). Um número em texto é aceito porque o envelope do
 * BFF pode serializá-lo assim; qualquer outro conteúdo equivale a campo sem
 * preenchimento. Zero é valor legítimo e não vira `null`.
 */
function readNumber(raw: Record<string, unknown>, key: string): number | null {
  const value = raw[key];

  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    return Number(value);
  }

  return null;
}

function toEmbarcacaoDimensoes(raw: unknown): EmbarcacaoDimensoes | null {
  if (!isRecord(raw)) return null;

  return {
    comprimentoTotal: readNumber(raw, "comprimentoTotal"),
    comprimentoEntrePerpendiculares: readNumber(raw, "comprimentoEntrePerpendiculares"),
    caladoMax: readNumber(raw, "caladoMax"),
    caladoDocagem: readNumber(raw, "caladoDocagem"),
    bocaMoldada: readNumber(raw, "bocaMoldada"),
    arqueacaoBruta: readNumber(raw, "arqueacaoBruta"),
    arqueacaoLiquida: readNumber(raw, "arqueacaoLiquida"),
    tonelagemPorteBruto: readNumber(raw, "tonelagemPorteBruto"),
    atualizacaoDimensoes: readValue(raw, "atualizacaoDimensoes"),
    comprimentoConves: readNumber(raw, "comprimentoConves"),
    larguraConves: readNumber(raw, "larguraConves"),
    areaTotalConves: readNumber(raw, "areaTotalConves"),
    resistenciaConves: readNumber(raw, "resistenciaConves"),
    capacidadeConves: readNumber(raw, "capacidadeConves"),
    comprimentoConvesLivreUtil: readNumber(raw, "comprimentoConvesLivreUtil"),
    larguraConvesLivreUtil: readNumber(raw, "larguraConvesLivreUtil"),
    areaConvesLivreUtil: readNumber(raw, "areaConvesLivreUtil"),
    atualizacaoCargoDeck: readValue(raw, "atualizacaoCargoDeck"),
    deslocamentoCaladoDocagem: readNumber(raw, "deslocamentoCaladoDocagem"),
    caladoHidrostaticoDocagem: readNumber(raw, "caladoHidrostaticoDocagem"),
    tamanhoApendices: readNumber(raw, "tamanhoApendices"),
    equipamentoRetratil: readValue(raw, "equipamentoRetratil"),
    caladoRealDocagem: readNumber(raw, "caladoRealDocagem"),
    atualizacaoDocagem: readValue(raw, "atualizacaoDocagem"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoTanques(raw: unknown): EmbarcacaoTanques | null {
  if (!isRecord(raw)) return null;

  return {
    capacidadeTotalCombustivel: readNumber(raw, "capacidadeTotalCombustivel"),
    capacidadeTotalAgua: readNumber(raw, "capacidadeTotalAgua"),
    capacidadeTotalOro: readNumber(raw, "capacidadeTotalOro"),
    capacidadeTotalAguaLastro: readNumber(raw, "capacidadeTotalAguaLastro"),
    capacidadeTotalOleoBase: readNumber(raw, "capacidadeTotalOleoBase"),
    capacidadeTotalSalmoura: readNumber(raw, "capacidadeTotalSalmoura"),
    capacidadeTotalLama: readNumber(raw, "capacidadeTotalLama"),
    capacidadeTotalGranel: readNumber(raw, "capacidadeTotalGranel"),
    comentarios: readValue(raw, "comentarios"),
    atualizacaoCapacidade: readValue(raw, "atualizacaoCapacidade"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoVazaoBombas(raw: unknown): EmbarcacaoVazaoBombas | null {
  if (!isRecord(raw)) return null;

  return {
    vazaoCombustivel: readNumber(raw, "vazaoCombustivel"),
    pressaoCombustivel: readNumber(raw, "pressaoCombustivel"),
    vazaoAgua: readNumber(raw, "vazaoAgua"),
    pressaoAgua: readNumber(raw, "pressaoAgua"),
    vazaoOro: readNumber(raw, "vazaoOro"),
    pressaoOro: readNumber(raw, "pressaoOro"),
    vazaoAguaLastro: readNumber(raw, "vazaoAguaLastro"),
    pressaoAguaLastro: readNumber(raw, "pressaoAguaLastro"),
    vazaoOleoBase: readNumber(raw, "vazaoOleoBase"),
    pressaoOleoBase: readNumber(raw, "pressaoOleoBase"),
    vazaoSalmoura: readNumber(raw, "vazaoSalmoura"),
    pressaoSalmoura: readNumber(raw, "pressaoSalmoura"),
    vazaoLama: readNumber(raw, "vazaoLama"),
    pressaoLama: readNumber(raw, "pressaoLama"),
    vazaoGranel: readNumber(raw, "vazaoGranel"),
    pressaoGranel: readNumber(raw, "pressaoGranel"),
    atualizacaoVazaoBomba: readValue(raw, "atualizacaoVazaoBomba"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoPropulsao(raw: unknown): EmbarcacaoPropulsao | null {
  if (!isRecord(raw)) return null;

  return {
    sistemaPropulsao: readValue(raw, "sistemaPropulsao"),
    fabricante: readValue(raw, "fabricante"),
    modelo: readValue(raw, "modelo"),
    eletricoOuMecanico: readValue(raw, "eletricoOuMecanico"),
    quantidade: readNumber(raw, "quantidade"),
    tipoGeracaoEnergia: readValue(raw, "tipoGeracaoEnergia"),
    potencia: readNumber(raw, "potencia"),
    marcaLinhaEixo: readValue(raw, "marcaLinhaEixo"),
    modeloLinhaEixo: readValue(raw, "modeloLinhaEixo"),
    atualizacaoPropulsao: readValue(raw, "atualizacaoPropulsao"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoEquipamentos(raw: unknown): EmbarcacaoEquipamentos | null {
  if (!isRecord(raw)) return null;

  return {
    possuiEstruturaFiFi: readValue(raw, "possuiEstruturaFiFi"),
    fiFiFuncionando: readValue(raw, "fiFiFuncionando"),
    possuiWatermaker: readValue(raw, "possuiWatermaker"),
    capacidadeWatermaker: readNumber(raw, "capacidadeWatermaker"),
    watermakerOperacional: readValue(raw, "watermakerOperacional"),
    atualizacaoEquipamentos: readValue(raw, "atualizacaoEquipamentos"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoContatos(raw: unknown): EmbarcacaoContatos | null {
  if (!isRecord(raw)) return null;

  return {
    celularEmbarcacao: readValue(raw, "celularEmbarcacao"),
    bridgeEmail: readValue(raw, "bridgeEmail"),
    captainEmail: readValue(raw, "captainEmail"),
    engineEmail: readValue(raw, "engineEmail"),
    chiefEngEmail: readValue(raw, "chiefEngEmail"),
    atualizacaoContato: readValue(raw, "atualizacaoContato"),
    gerenteFrota: readValue(raw, "gerenteFrota"),
    contatoGerenteFrota: readValue(raw, "contatoGerenteFrota"),
    coordenadorFrota: readValue(raw, "coordenadorFrota"),
    contatoCoordenadorFrota: readValue(raw, "contatoCoordenadorFrota"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoImagens(raw: unknown): EmbarcacaoImagens | null {
  if (!isRecord(raw)) return null;

  return {
    urlImagem: readValue(raw, "urlImagem"),
    urlImagemUhd: readValue(raw, "urlImagemUhd"),
    atualizacaoImagens: readValue(raw, "atualizacaoImagens"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

/** Arquivo de uma imagem da embarcação, entregue pelo hub-bff como anexo; apenas GET. */
export function buildEmbarcacaoImagemDownloadPath(id: string, tipo: EmbarcacaoImagemTipo): string {
  return `${EMBARCACOES_PATHS.LIST}/${encodeURIComponent(id)}/imagens/${tipo}/download`;
}

/**
 * Baixa o arquivo da imagem pelo hub-bff, que o busca no site de origem. O front não faz isso
 * direto porque o site não envia CORS para o Hub; sem isso o navegador bloqueia a leitura.
 */
export async function downloadEmbarcacaoImagem({
  id,
  tipo,
  signal,
}: DownloadEmbarcacaoImagemParams): Promise<EmbarcacaoImagemArquivo> {
  const response = await getHttpClient().get<ArrayBuffer>(
    buildEmbarcacaoImagemDownloadPath(id, tipo),
    { signal, responseType: "arraybuffer" },
  );

  return {
    content: new Uint8Array(response.data),
    fileName: parseContentDispositionFileName(response.headers?.["content-disposition"]),
    mimeType: response.headers?.["content-type"] ?? "",
  };
}

function toEmbarcacaoVelocidadeConsumo(raw: unknown): EmbarcacaoVelocidadeConsumo | null {
  if (!isRecord(raw)) return null;

  return {
    velMaximaBarco: readNumber(raw, "velMaximaBarco"),
    consumoVelMaximaBarco: readNumber(raw, "consumoVelMaximaBarco"),
    velServico: readNumber(raw, "velServico"),
    consumoVelServico: readNumber(raw, "consumoVelServico"),
    velEconomica: readNumber(raw, "velEconomica"),
    consumoVelEconomica: readNumber(raw, "consumoVelEconomica"),
    atualizacaoVelocidadeConsumo: readValue(raw, "atualizacaoVelocidadeConsumo"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoSistemasDp(raw: unknown): EmbarcacaoSistemasDp | null {
  if (!isRecord(raw)) return null;

  return {
    dp: readValue(raw, "dp"),
    fabricanteModelo: readValue(raw, "fabricanteModelo"),
    sistemaReferenciaRelativo1: readValue(raw, "sistemaReferenciaRelativo1"),
    sistemaReferenciaRelativo2: readValue(raw, "sistemaReferenciaRelativo2"),
    atualizacaoDp: readValue(raw, "atualizacaoDp"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoAcomodacaoTipo(raw: unknown): EmbarcacaoAcomodacaoTipo | null {
  if (!isRecord(raw)) return null;

  return {
    tipo: readValue(raw, "tipo"),
    quantidade: readNumber(raw, "quantidade"),
    quantoComporta: readNumber(raw, "quantoComporta"),
    comentario: readValue(raw, "comentario"),
  };
}

function toEmbarcacaoAcomodacoes(raw: unknown): EmbarcacaoAcomodacoes | null {
  if (!isRecord(raw)) return null;

  const acomodacoes = extractItems(raw.acomodacoes)
    .map(toEmbarcacaoAcomodacaoTipo)
    .filter((tipo): tipo is EmbarcacaoAcomodacaoTipo => tipo !== null);

  return {
    possuiEnfermaria: readValue(raw, "possuiEnfermaria"),
    possuiAcademia: readValue(raw, "possuiAcademia"),
    atualizacaoAcomodacao: readValue(raw, "atualizacaoAcomodacao"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
    acomodacoes,
  };
}

function toEmbarcacaoMotor(raw: unknown): EmbarcacaoMotor | null {
  if (!isRecord(raw)) return null;

  return {
    motor: readValue(raw, "motor"),
    marca: readValue(raw, "marca"),
    modelo: readValue(raw, "modelo"),
    numeroSerie: readValue(raw, "numeroSerie"),
    codigoTmMaster: readValue(raw, "codigoTmMaster"),
    empresaAutomacaoResponsavel: readValue(raw, "empresaAutomacaoResponsavel"),
    potencia: readNumber(raw, "potencia"),
    rpm: readNumber(raw, "rpm"),
    tbo: readNumber(raw, "tbo"),
    periodicidade: readNumber(raw, "periodicidade"),
    ativo: readValue(raw, "ativo"),
    ordemMotor: readNumber(raw, "ordemMotor"),
    atualizacaoMotores: readValue(raw, "atualizacaoMotores"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoGuinchoGuindaste(raw: unknown): EmbarcacaoGuinchoGuindaste | null {
  if (!isRecord(raw)) return null;

  return {
    guinchoGuindaste: readValue(raw, "guinchoGuindaste"),
    tipo: readValue(raw, "tipo"),
    modelo: readValue(raw, "modelo"),
    alcance: readNumber(raw, "alcance"),
    ahc: readValue(raw, "ahc"),
    profundidade: readNumber(raw, "profundidade"),
    capacidadeCarga: readNumber(raw, "capacidadeCarga"),
    diametroCabo: readNumber(raw, "diametroCabo"),
    comprimentoCabo: readNumber(raw, "comprimentoCabo"),
    materialCabo: readValue(raw, "materialCabo"),
    capacidadeCabo: readValue(raw, "capacidadeCabo"),
    pesoCabo: readValue(raw, "pesoCabo"),
    atualizacaoGuinchoGuindaste: readValue(raw, "atualizacaoGuinchoGuindaste"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoThruster(raw: unknown): EmbarcacaoThruster | null {
  if (!isRecord(raw)) return null;

  return {
    thruster: readValue(raw, "thruster"),
    quantidade: readNumber(raw, "quantidade"),
    marca: readValue(raw, "marca"),
    numeroSerie: readValue(raw, "numeroSerie"),
    potencia: readNumber(raw, "potencia"),
    atualizacaoThrusters: readValue(raw, "atualizacaoThrusters"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoClasse(raw: unknown): EmbarcacaoClasse | null {
  if (!isRecord(raw)) return null;

  return {
    notacaoClasse: readValue(raw, "notacaoClasse"),
    validadeNotacaoClasse: readValue(raw, "validadeNotacaoClasse"),
    classificadoraAtual: readValue(raw, "classificadoraAtual"),
    ultimoReportClasse: readValue(raw, "ultimoReportClasse"),
    anualFrom: readValue(raw, "anualFrom"),
    anualTo: readValue(raw, "anualTo"),
    especialFrom: readValue(raw, "especialFrom"),
    especialTo: readValue(raw, "especialTo"),
    ultimaEspecial: readValue(raw, "ultimaEspecial"),
    intermediariaFrom: readValue(raw, "intermediariaFrom"),
    intermediariaTo: readValue(raw, "intermediariaTo"),
    ultimaIntermediaria: readValue(raw, "ultimaIntermediaria"),
    ultimaBottom: readValue(raw, "ultimaBottom"),
    condicaoUltimaBottom: readValue(raw, "condicaoUltimaBottom"),
    bottomTo: readValue(raw, "bottomTo"),
    thrusterTo: readValue(raw, "thrusterTo"),
    propulsaoTo: readValue(raw, "propulsaoTo"),
    ultimoFmea: readValue(raw, "ultimoFmea"),
    vencimentoFmea: readValue(raw, "vencimentoFmea"),
    atualizacaoClasse: readValue(raw, "atualizacaoClasse"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao"),
  };
}

function toEmbarcacaoComercial(raw: unknown): EmbarcacaoComercial | null {
  if (!isRecord(raw)) return null;

  return {
    clienteAtual: readValue(raw, "clienteAtual"),
    observacoes: readValue(raw, "observacoes"),
    inicioContrato: readValue(raw, "inicioContrato"),
    fimContrato: readValue(raw, "fimContrato"),
    extensaoContrato: readValue(raw, "extensaoContrato"),
    atualizacaoComercial: readValue(raw, "atualizacaoComercial"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

function toEmbarcacaoConsumoOperacao(raw: unknown): EmbarcacaoConsumoOperacao | null {
  if (!isRecord(raw)) return null;

  return {
    consumoDp: readValue(raw, "consumoDp"),
    consumoFdAt: readValue(raw, "consumoFdAt"),
    consumoNvVelServico: readValue(raw, "consumoNvVelServico"),
    consumoNvVelTotal: readValue(raw, "consumoNvVelTotal"),
    consumoSm: readValue(raw, "consumoSm"),
    consumoNvVelEconomica: readValue(raw, "consumoNvVelEconomica"),
    atualizacaoConsumo: readValue(raw, "atualizacaoConsumo", "dataAtualizacaoConsumo"),
    ultimaAtualizacao: readValue(raw, "ultimaAtualizacao", "dataUltimaAtualizacao"),
  };
}

/**
 * A ficha completa chegou fora do contrato (bloco ausente ou com outro tipo).
 * Não é lida como seção vazia: a tela apresenta a falha de carga.
 */
export class EmbarcacaoFichaInvalidaError extends Error {
  constructor(detail: string) {
    super(`Resposta inválida da ficha da embarcação: ${detail}.`);
    this.name = "EmbarcacaoFichaInvalidaError";
  }
}

export function isEmbarcacaoFichaInvalida(error: unknown): error is EmbarcacaoFichaInvalidaError {
  return error instanceof EmbarcacaoFichaInvalidaError;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return isRecord(value) && !Array.isArray(value);
}

function requireObjectBlock(data: Record<string, unknown>, key: string): Record<string, unknown> {
  const block = data[key];
  if (!isObject(block))
    throw new EmbarcacaoFichaInvalidaError(`o bloco "${key}" deve ser um objeto`);

  return block;
}

function requireObjectList(value: unknown, label: string): Record<string, unknown>[] {
  if (!Array.isArray(value)) throw new EmbarcacaoFichaInvalidaError(`${label} deve ser uma lista`);

  const items: unknown[] = value;

  return items.map((item, index) => {
    if (!isObject(item)) {
      throw new EmbarcacaoFichaInvalidaError(`o item ${index} de ${label} deve ser um objeto`);
    }

    return item;
  });
}

function parseObjectBlock<T>(
  data: Record<string, unknown>,
  key: string,
  parse: (raw: unknown) => T | null,
): T {
  const parsed = parse(requireObjectBlock(data, key));
  if (parsed === null) throw new EmbarcacaoFichaInvalidaError(`o bloco "${key}" é ilegível`);

  return parsed;
}

function parseListBlock<T>(
  data: Record<string, unknown>,
  key: string,
  parse: (raw: unknown) => T | null,
): T[] {
  return requireObjectList(data[key], `o bloco "${key}"`).map((item) => {
    const parsed = parse(item);
    if (parsed === null) throw new EmbarcacaoFichaInvalidaError(`um item de "${key}" é ilegível`);

    return parsed;
  });
}

/**
 * A lista de tipos de acomodação vem dentro do objeto e é `null` quando a embarcação não tem registro
 * (lida como lista vazia, estado vazio da seção); fora isso, deve ser uma lista de objetos.
 */
function parseAcomodacoesBlock(data: Record<string, unknown>): EmbarcacaoAcomodacoes {
  const block = requireObjectBlock(data, "acomodacoes");
  if (block.acomodacoes !== null) {
    requireObjectList(block.acomodacoes, 'a lista "acomodacoes.acomodacoes"');
  }

  return parseObjectBlock(data, "acomodacoes", toEmbarcacaoAcomodacoes);
}

const IMAGENS_VAZIAS: EmbarcacaoImagens = {
  urlImagem: null,
  urlImagemUhd: null,
  atualizacaoImagens: null,
  ultimaAtualizacao: null,
};

/** `embarcacao_imagem` é 1:N: a seção mostra o primeiro registro, e a lista vazia é seção vazia. */
function parseImagensBlock(data: Record<string, unknown>): EmbarcacaoImagens {
  const [primeira] = requireObjectList(data.imagens, 'o bloco "imagens"');

  return (primeira && toEmbarcacaoImagens(primeira)) ?? IMAGENS_VAZIAS;
}

/** As 17 chaves de `data` da ficha; nenhuma outra é aceita. */
const FICHA_KEYS: ReadonlySet<string> = new Set([
  "bases-tecnicas",
  "classes",
  "dimensoes",
  "consumo-operacao",
  "velocidades-consumos",
  "propulsao",
  "acomodacoes",
  "vazao-bombas",
  "sistemas-dp",
  "motores",
  "thrusters",
  "tanques",
  "contatos",
  "equipamentos",
  "guinchos-guindastes",
  "imagens",
  "comerciais",
]);

function toEmbarcacaoFicha(payload: unknown): EmbarcacaoFicha {
  if (!isObject(payload) || payload.success === false || !isObject(payload.data)) {
    throw new EmbarcacaoFichaInvalidaError("o envelope não traz o objeto data");
  }

  const { data } = payload;
  const unexpected = Object.keys(data).filter((key) => !FICHA_KEYS.has(key));
  if (unexpected.length > 0) {
    throw new EmbarcacaoFichaInvalidaError(`chaves fora do contrato: ${unexpected.join(", ")}`);
  }

  return {
    baseTecnica: parseObjectBlock(data, "bases-tecnicas", toEmbarcacaoBaseTecnica),
    classe: parseObjectBlock(data, "classes", toEmbarcacaoClasse),
    dimensoes: parseObjectBlock(data, "dimensoes", toEmbarcacaoDimensoes),
    consumoOperacao: parseObjectBlock(data, "consumo-operacao", toEmbarcacaoConsumoOperacao),
    velocidadeConsumo: parseObjectBlock(
      data,
      "velocidades-consumos",
      toEmbarcacaoVelocidadeConsumo,
    ),
    propulsao: parseObjectBlock(data, "propulsao", toEmbarcacaoPropulsao),
    acomodacoes: parseAcomodacoesBlock(data),
    vazaoBombas: parseObjectBlock(data, "vazao-bombas", toEmbarcacaoVazaoBombas),
    sistemasDp: parseObjectBlock(data, "sistemas-dp", toEmbarcacaoSistemasDp),
    motores: parseListBlock(data, "motores", toEmbarcacaoMotor),
    thrusters: parseListBlock(data, "thrusters", toEmbarcacaoThruster),
    tanques: parseObjectBlock(data, "tanques", toEmbarcacaoTanques),
    contatos: parseObjectBlock(data, "contatos", toEmbarcacaoContatos),
    equipamentos: parseObjectBlock(data, "equipamentos", toEmbarcacaoEquipamentos),
    guinchosGuindastes: parseListBlock(data, "guinchos-guindastes", toEmbarcacaoGuinchoGuindaste),
    imagens: parseImagensBlock(data),
    comercial: parseObjectBlock(data, "comerciais", toEmbarcacaoComercial),
  };
}

/**
 * Ficha completa da embarcação acionada na lupa da listagem.
 *
 * `GET /api/embarcacoes/{id}/all`, somente leitura: uma única consulta traz as 17 seções do
 * detalhamento. O envelope é `{ success, message, data, meta }` e cada chave de `data` é o bloco da
 * seção já mapeado pelo hub-bff, com o nome do segmento da rota individual que ele substitui. Bloco
 * ausente ou de outro tipo, e qualquer chave a mais, rejeitam a ficha inteira com
 * `EmbarcacaoFichaInvalidaError`: nada vira seção vazia.
 */
export async function getEmbarcacaoFicha({
  id,
  signal,
}: GetEmbarcacaoFichaParams): Promise<EmbarcacaoFicha> {
  const config: HttpRequestConfig = { signal };

  const response = await getHttpClient().get<unknown>(buildEmbarcacaoFichaPath(id), config);

  return toEmbarcacaoFicha(response.data);
}
